import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Facebook, Instagram, Mail, MessageCircle, Youtube, ExternalLink,
  Save, Sparkles, Send, Image as ImageIcon, Paperclip, Smile,
  CheckCheck, AlertCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminSocial — the "Social Hub" inside the admin dashboard.
 *
 * Two tabs:
 *   1. Quick Launchers — stores the admin's social handles once and
 *      exposes one-tap deep-link cards that open the Page inbox /
 *      Instagram DMs / Gmail / Outlook in a new tab. Works today, no
 *      API approvals needed, zero credential handling.
 *
 *   2. Full Inbox (Preview) — a mocked unified inbox that mixes
 *      Facebook, Instagram and Gmail threads together with a reply
 *      composer. Clearly ribboned "PREVIEW MODE" so admins can show
 *      prospective clients what the fully-integrated version will look
 *      like once Meta / Google app approvals land.
 *
 * Backend: GET/PATCH /admin/social/accounts stores the handles on the
 * user doc. The preview tab is pure client-side sample data \u2014 no
 * credentials are collected until the real OAuth flow ships.
 */
export default function AdminSocial() {
  const [accounts, setAccounts] = useState({
    facebook_page_url: '', instagram_handle: '',
    gmail_address: '', outlook_address: '',
    tiktok_handle: '', youtube_channel_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/social/accounts')
      .then((r) => setAccounts({ ...accounts, ...(r.data || {}) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAccounts = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/social/accounts', accounts);
      toast.success('Social handles saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // ---- Deep-link builders -----------------------------------------
  // Each returns a URL that opens the native inbox in a new tab. When
  // the handle is empty we fall back to the platform's generic inbox.
  const launcherLinks = () => {
    const normalizeFbUrl = (u) => {
      if (!u) return 'https://www.facebook.com/business/latest/inbox';
      if (u.startsWith('http')) {
        // Append the Page's inbox path when possible
        return u.endsWith('/') ? `${u}inbox` : `${u}/inbox`;
      }
      return `https://www.facebook.com/${u}/inbox`;
    };
    const normalizeIg = (h) => {
      // Instagram's web inbox requires login \u2014 the handle is just for display
      return 'https://www.instagram.com/direct/inbox/';
    };
    const normalizeGmail = (e) => {
      if (!e) return 'https://mail.google.com/mail/';
      return `https://mail.google.com/mail/u/${encodeURIComponent(e)}/#inbox`;
    };
    const normalizeOutlook = () => 'https://outlook.office.com/mail/';
    return {
      facebook:  normalizeFbUrl(accounts.facebook_page_url),
      instagram: normalizeIg(accounts.instagram_handle),
      gmail:     normalizeGmail(accounts.gmail_address),
      outlook:   normalizeOutlook(accounts.outlook_address),
      tiktok:    accounts.tiktok_handle ? `https://www.tiktok.com/@${accounts.tiktok_handle.replace('@','')}/inbox` : 'https://www.tiktok.com/messages',
      youtube:   accounts.youtube_channel_url || 'https://studio.youtube.com/channel/UC/comments',
    };
  };
  const links = launcherLinks();

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto' data-testid='admin-social-page'>
      {/* Header */}
      <div className='mb-5'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Social Hub</div>
        <h1 className='font-display text-2xl sm:text-3xl font-bold mt-1 text-slate-900 dark:text-slate-50'>
          Messages & Comments
        </h1>
        <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>
          Jump straight to every inbox from one place &mdash; and preview the fully-integrated
          reply-from-dashboard experience your clients get on the premium plan.
        </p>
      </div>

      <Tabs defaultValue='launchers' className='w-full'>
        <TabsList className='mb-4'>
          <TabsTrigger value='launchers' data-testid='social-tab-launchers'>Quick Launchers</TabsTrigger>
          <TabsTrigger value='preview' data-testid='social-tab-preview'>
            Full Inbox
            <Badge className='ml-2 bg-[#C8102E] text-white border-0 text-[9px] uppercase tracking-wider'>Preview</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ========== Tab 1 \u2014 Quick launchers ========== */}
        <TabsContent value='launchers'>
          {/* Handles config card */}
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 mb-5' data-testid='social-handles-card'>
            <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50 mb-1'>
              Your social handles
            </h3>
            <p className='text-xs text-slate-500 dark:text-slate-400 mb-4'>
              Save once &mdash; launchers below will deep-link straight to your Page / inbox.
            </p>
            <div className='grid sm:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='fb-url' className='text-xs flex items-center gap-1.5'>
                  <Facebook className='w-3.5 h-3.5 text-[#1877F2]' /> Facebook Page URL
                </Label>
                <Input id='fb-url' placeholder='https://www.facebook.com/YourPage'
                  value={accounts.facebook_page_url || ''}
                  onChange={(e) => setAccounts({ ...accounts, facebook_page_url: e.target.value })}
                  data-testid='social-fb-input' />
              </div>
              <div>
                <Label htmlFor='ig' className='text-xs flex items-center gap-1.5'>
                  <Instagram className='w-3.5 h-3.5 text-[#E4405F]' /> Instagram handle
                </Label>
                <Input id='ig' placeholder='k9os_app'
                  value={accounts.instagram_handle || ''}
                  onChange={(e) => setAccounts({ ...accounts, instagram_handle: e.target.value })}
                  data-testid='social-ig-input' />
              </div>
              <div>
                <Label htmlFor='gmail' className='text-xs flex items-center gap-1.5'>
                  <Mail className='w-3.5 h-3.5 text-[#EA4335]' /> Gmail address
                </Label>
                <Input id='gmail' placeholder='hello@yourbusiness.com'
                  value={accounts.gmail_address || ''}
                  onChange={(e) => setAccounts({ ...accounts, gmail_address: e.target.value })}
                  data-testid='social-gmail-input' />
              </div>
              <div>
                <Label htmlFor='outlook' className='text-xs flex items-center gap-1.5'>
                  <Mail className='w-3.5 h-3.5 text-[#0078D4]' /> Outlook / Microsoft 365
                </Label>
                <Input id='outlook' placeholder='you@outlook.com'
                  value={accounts.outlook_address || ''}
                  onChange={(e) => setAccounts({ ...accounts, outlook_address: e.target.value })}
                  data-testid='social-outlook-input' />
              </div>
              <div>
                <Label htmlFor='tiktok' className='text-xs flex items-center gap-1.5'>
                  <MessageCircle className='w-3.5 h-3.5 text-[#000]' /> TikTok handle
                </Label>
                <Input id='tiktok' placeholder='@yourbusiness'
                  value={accounts.tiktok_handle || ''}
                  onChange={(e) => setAccounts({ ...accounts, tiktok_handle: e.target.value })}
                  data-testid='social-tiktok-input' />
              </div>
              <div>
                <Label htmlFor='yt' className='text-xs flex items-center gap-1.5'>
                  <Youtube className='w-3.5 h-3.5 text-[#FF0000]' /> YouTube channel URL
                </Label>
                <Input id='yt' placeholder='https://studio.youtube.com/channel/UCxxxx'
                  value={accounts.youtube_channel_url || ''}
                  onChange={(e) => setAccounts({ ...accounts, youtube_channel_url: e.target.value })}
                  data-testid='social-yt-input' />
              </div>
            </div>
            <Button
              className='mt-4 bg-[#1B3A87] hover:bg-[#16306F] text-white font-semibold'
              onClick={saveAccounts}
              disabled={saving || loading}
              data-testid='social-save-handles-btn'
            >
              <Save className='w-4 h-4 mr-2' /> {saving ? 'Saving\u2026' : 'Save handles'}
            </Button>
          </Card>

          {/* Launcher grid */}
          <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {[
              { k: 'facebook',  name: 'Facebook Messages', brand: '#1877F2', icon: Facebook, href: links.facebook, hint: accounts.facebook_page_url || 'Opens Facebook Business inbox' },
              { k: 'instagram', name: 'Instagram DMs',     brand: '#E4405F', icon: Instagram, href: links.instagram, hint: accounts.instagram_handle ? `@${accounts.instagram_handle.replace('@','')}` : 'Opens Instagram inbox' },
              { k: 'gmail',     name: 'Gmail Inbox',       brand: '#EA4335', icon: Mail, href: links.gmail, hint: accounts.gmail_address || 'Opens Gmail inbox' },
              { k: 'outlook',   name: 'Outlook',           brand: '#0078D4', icon: Mail, href: links.outlook, hint: accounts.outlook_address || 'Opens Outlook on the web' },
              { k: 'tiktok',    name: 'TikTok Messages',   brand: '#111',    icon: MessageCircle, href: links.tiktok, hint: accounts.tiktok_handle ? `@${accounts.tiktok_handle.replace('@','')}` : 'Opens TikTok inbox' },
              { k: 'youtube',   name: 'YouTube Comments',  brand: '#FF0000', icon: Youtube, href: links.youtube, hint: accounts.youtube_channel_url ? 'Your channel' : 'Opens YouTube Studio' },
            ].map((p) => (
              <a
                key={p.k}
                href={p.href}
                target='_blank'
                rel='noopener noreferrer'
                className='group block'
                data-testid={`social-launcher-${p.k}`}
              >
                <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-500 hover:-translate-y-0.5 transition-all shadow-sm hover:shadow-lg'>
                  <div className='flex items-start justify-between gap-3'>
                    <div
                      className='w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md shrink-0'
                      style={{ background: p.brand }}
                    >
                      <p.icon className='w-5 h-5' />
                    </div>
                    <ExternalLink className='w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 shrink-0' />
                  </div>
                  <div className='mt-3'>
                    <div className='font-display font-semibold text-slate-900 dark:text-slate-50'>{p.name}</div>
                    <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>{p.hint}</div>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </TabsContent>

        {/* ========== Tab 2 \u2014 Full Inbox Preview ========== */}
        <TabsContent value='preview'>
          <InboxPreview />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------------------------------------------------------
 * InboxPreview \u2014 fully-styled MOCK of the unified inbox.
 * No real API calls \u2014 sample data lives in-component. Clearly
 * labelled "Preview mode" so prospective clients can see the
 * capability during sales demos without implying it's live yet.
 * ---------------------------------------------------------------*/
const SAMPLE_THREADS = [
  {
    id: 't1', channel: 'facebook', name: 'Monica Reyes', handle: 'Facebook',
    preview: 'Hey! Is Max still able to board the weekend of the 14th?',
    unread: true, time: '2m',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop',
    messages: [
      { from: 'them', body: 'Hey! Is Max still able to board the weekend of the 14th?', time: '09:12' },
      { from: 'them', body: 'He loves it there \u2014 we don\u2019t want to miss out.', time: '09:12' },
    ],
  },
  {
    id: 't2', channel: 'instagram', name: 'Jake Carter', handle: '@jakec',
    preview: 'Loved the training reel! Where do you train?',
    unread: true, time: '14m',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop',
    messages: [
      { from: 'them', body: 'Loved the training reel! Where do you train?', time: '08:58' },
    ],
  },
  {
    id: 't3', channel: 'gmail', name: 'Allison Ng', handle: 'allison@fetchclub.co',
    preview: 'Quote request \u2014 20 pack board & train for March',
    unread: false, time: '1h',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop',
    messages: [
      { from: 'them', body: 'Hi Greg \u2014 could you send a quote for a 20-pack board & train?', time: '08:02' },
      { from: 'me', body: 'Hey Allison, happy to! Sending a full breakdown shortly.', time: '08:14' },
    ],
  },
  {
    id: 't4', channel: 'facebook', name: 'Darnell Price', handle: 'Facebook',
    preview: 'Thanks for the fast reply!',
    unread: false, time: '3h',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop',
    messages: [
      { from: 'me', body: 'We can get you in on Thursday at 10am.', time: '06:30' },
      { from: 'them', body: 'Thanks for the fast reply!', time: '06:33' },
    ],
  },
  {
    id: 't5', channel: 'instagram', name: 'Priya Shah', handle: '@priyashah',
    preview: 'What breeds do you specialize in?',
    unread: false, time: '1d',
    avatar: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=80&h=80&fit=crop',
    messages: [
      { from: 'them', body: 'What breeds do you specialize in?', time: 'Yesterday' },
    ],
  },
];

const CHANNEL_META = {
  facebook:  { label: 'Facebook',  brand: '#1877F2', icon: Facebook },
  instagram: { label: 'Instagram', brand: '#E4405F', icon: Instagram },
  gmail:     { label: 'Gmail',     brand: '#EA4335', icon: Mail },
};

const InboxPreview = () => {
  const [active, setActive] = useState(SAMPLE_THREADS[0]);
  const [filter, setFilter] = useState('all');
  const [reply, setReply] = useState('');

  const filtered = SAMPLE_THREADS.filter((t) => filter === 'all' || t.channel === filter);

  const sendReply = (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    // Preview mode \u2014 just toast success. No persistence.
    toast.success('(Preview) Reply would send via the integrated API');
    setReply('');
  };

  return (
    <Card className='p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden' data-testid='social-inbox-preview'>
      {/* Preview-mode ribbon \u2014 makes it clear this isn't live yet. */}
      <div className='bg-gradient-to-r from-[#C8102E] to-[#A50D26] text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-xs'>
        <div className='flex items-center gap-2'>
          <Sparkles className='w-4 h-4' />
          <strong className='uppercase tracking-wider font-bold'>Preview mode</strong>
          <span className='opacity-90'>&middot; Sample data &mdash; shows what the Premium Inbox will look like once Facebook / Google approvals land.</span>
        </div>
        <span className='opacity-80 hidden sm:inline'>No real messages are sent</span>
      </div>

      <div className='grid lg:grid-cols-[320px_1fr] min-h-[560px]'>
        {/* Threads list */}
        <div className='border-r border-slate-200 dark:border-slate-800 flex flex-col'>
          {/* Channel filter */}
          <div className='p-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-1.5'>
            {[
              { k: 'all',       label: 'All' },
              { k: 'facebook',  label: 'FB' },
              { k: 'instagram', label: 'IG' },
              { k: 'gmail',     label: 'Email' },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                  filter === f.k
                    ? 'bg-[#1B3A87] text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                data-testid={`inbox-filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className='flex-1 overflow-y-auto'>
            {filtered.map((t) => {
              const meta = CHANNEL_META[t.channel];
              const Icon = meta.icon;
              const isActive = active.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t)}
                  className={`w-full text-left px-3 py-3 border-b border-slate-100 dark:border-slate-800 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                    isActive ? 'bg-slate-50 dark:bg-slate-800' : ''
                  }`}
                  data-testid={`inbox-thread-${t.id}`}
                >
                  <div className='relative shrink-0'>
                    <img src={t.avatar} alt={t.name} className='w-10 h-10 rounded-full object-cover' />
                    <div
                      className='absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900'
                      style={{ background: meta.brand }}
                    >
                      <Icon className='w-2.5 h-2.5 text-white' />
                    </div>
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='font-semibold text-sm text-slate-900 dark:text-slate-50 truncate'>{t.name}</div>
                      <div className='text-[10px] text-slate-400 shrink-0 tabular-nums'>{t.time}</div>
                    </div>
                    <div className={`text-xs truncate ${t.unread ? 'font-semibold text-slate-900 dark:text-slate-50' : 'text-slate-500 dark:text-slate-400'}`}>
                      {t.preview}
                    </div>
                  </div>
                  {t.unread && <span className='w-2 h-2 rounded-full bg-[#C8102E] mt-1.5 shrink-0' />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active thread + composer */}
        <div className='flex flex-col'>
          {/* Thread header */}
          <div className='p-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3'>
            <img src={active.avatar} alt='' className='w-10 h-10 rounded-full object-cover' />
            <div className='flex-1 min-w-0'>
              <div className='font-display font-semibold text-slate-900 dark:text-slate-50'>{active.name}</div>
              <div className='flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400'>
                {(() => { const M = CHANNEL_META[active.channel]; const I = M.icon; return <><I className='w-3 h-3' style={{ color: M.brand }} /> {M.label} &middot; {active.handle}</>; })()}
              </div>
            </div>
            <Badge className='bg-emerald-500 text-white border-0 text-[10px]'><CheckCheck className='w-3 h-3 mr-1' /> Linked</Badge>
          </div>

          {/* Thread body */}
          <div className='flex-1 p-4 space-y-2 overflow-y-auto bg-slate-50 dark:bg-slate-800/50'>
            {active.messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                  m.from === 'me'
                    ? 'bg-[#1B3A87] text-white rounded-br-none'
                    : 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-700 rounded-bl-none'
                }`}>
                  {m.body}
                  <div className={`text-[9px] mt-1 ${m.from === 'me' ? 'text-slate-200' : 'text-slate-400'}`}>{m.time}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <form onSubmit={sendReply} className='p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900' data-testid='inbox-composer'>
            <div className='flex items-end gap-2'>
              <div className='flex gap-1'>
                <Button type='button' size='sm' variant='ghost' className='h-9 w-9 p-0' aria-label='Attach image' data-testid='inbox-attach-image'>
                  <ImageIcon className='w-4 h-4' />
                </Button>
                <Button type='button' size='sm' variant='ghost' className='h-9 w-9 p-0' aria-label='Attach file' data-testid='inbox-attach-file'>
                  <Paperclip className='w-4 h-4' />
                </Button>
                <Button type='button' size='sm' variant='ghost' className='h-9 w-9 p-0 hidden sm:inline-flex' aria-label='Insert emoji'>
                  <Smile className='w-4 h-4' />
                </Button>
              </div>
              <Textarea
                rows={1}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Reply to ${active.name} via ${CHANNEL_META[active.channel].label}\u2026`}
                className='resize-none min-h-10'
                data-testid='inbox-reply-input'
              />
              <Button
                type='submit'
                className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold'
                data-testid='inbox-send-btn'
              >
                <Send className='w-4 h-4 mr-1.5' /> Send
              </Button>
            </div>
            <p className='text-[10px] text-slate-400 dark:text-slate-500 mt-2 flex items-center gap-1'>
              <AlertCircle className='w-3 h-3' />
              Preview mode &mdash; your reply would normally post via the connected channel&rsquo;s API.
            </p>
          </form>
        </div>
      </div>

      {/* Explainer footer */}
      <div className='border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-4 text-xs text-slate-600 dark:text-slate-300'>
        <div className='font-semibold text-slate-900 dark:text-slate-50 mb-1 flex items-center gap-1.5'>
          <Clock className='w-3.5 h-3.5' /> Enabling the real inbox
        </div>
        Facebook &amp; Instagram require a Meta Business app with <code className='px-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'>pages_messaging</code> &amp;
        {' '}<code className='px-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'>instagram_manage_messages</code> permissions (approval ~2&ndash;6 wks).
        Gmail requires Google OAuth sensitive-scope verification. Once approved, each thread above becomes a live two-way channel.
      </div>
    </Card>
  );
};
